import React, { FC, useContext, memo } from 'react';
import { Box, Typography, Avatar, Divider, Grid, FormControlLabel, Checkbox } from '@mui/material';
import { LocationOn, Phone, Email, Language } from '@mui/icons-material';
import { useEditLab } from '../../../context/edit-lab-context';
import { AppToasterContext } from '../../shared/app-toaster/app-toaster-context';
import FormattedAddress from 'components/shared/address/formatted-address.component';
import SkeletonCard from 'components/shared/skeleton/skeleton-card.component';
import { useTranslation } from 'react-i18next';
import { LoadingButton } from '@mui/lab';
import { customerSettings } from 'utils/customerSettings';
import { CONSTANTS } from 'config/constants';
import { currentOrgVar } from 'apollo/reactive-variables/organization';
import CurrencyInput from 'components/shared/currency-input/currency-input.component';
import { Currency } from 'models/country';

const LabInfo: FC = memo(() => {
    const { t: translate } = useTranslation(['common', 'organization']);
    const {
        lab,
        isDisabled,
        isDisableLoading,
        isLoading,
        toggleLabStatus,
        isRequireException,
        isTioTandvardEnabled,
        isRequireExceptionLoading,
        toggleRequireException,
        toggleTioTandvardEnabled,
        isTandvardEnabledLoading,
        currency,
        defaultActionSetCurrency,
        isCurrencyLoading,
        updateConnectedLabCurrency,
    } = useEditLab();

    const { setAppToasterContent } = useContext(AppToasterContext);

    const currentOrg = currentOrgVar();

    const isTioTandvardPricingEnabled = customerSettings.getCustomerSetting(
        currentOrg.customerSettings,
        CONSTANTS.SETTINGS_NAMES.ENABLE_TIOTANDVARD_PRICING,
    );

    const showToast = (severity: 'success' | 'error', message: string) =>
        setAppToasterContent({ isOpen: true, severity, message });

    const handleToggleLabStatus = async () => {
        try {
            await toggleLabStatus(!isDisabled);
            showToast(
                'success',
                !isDisabled ? translate('organization:LAB_DISABLED') : translate('organization:LAB_ENABLED'),
            );
        } catch {
            showToast('error', translate('organization:FAILED_TO_UPDATE_LAB'));
        }
    };

    const handleToggleRequireException = async (checked: boolean) => {
        try {
            await toggleRequireException(!checked);
            showToast(
                'success',
                checked
                    ? translate('organization:EXCEPTION_REQUIREMENT_DISABLED')
                    : translate('organization:EXCEPTION_REQUIREMENT_ENABLED'),
            );
        } catch {
            showToast('error', translate('organization:FAILED_TO_UPDATE_LAB'));
        }
    };

    const handleToggleTandvardEnabled = async (checked: boolean) => {
        try {
            await toggleTioTandvardEnabled(checked);
            showToast(
                'success',
                checked
                    ? translate('organization:TIOTANDVARD_PRICE_LIST_ENABLED')
                    : translate('organization:TIOTANDVARD_PRICE_LIST_DISABLED'),
            );
        } catch {
            showToast('error', translate('organization:FAILED_TO_UPDATE_LAB'));
        }
    };

    const handleCurrencySelect = async (selectedCurrency: Currency) => {
        try {
            await updateConnectedLabCurrency(selectedCurrency.code);
            showToast('success', translate('organization:CURRENCY_UPDATED'));
        } catch {
            showToast('error', translate('organization:FAILED_TO_UPDATE_CURRENCY'));
        }
    };

    if (isLoading)
        return (
            <SkeletonCard
                hasAvatar={true}
                hasTitle={true}
                hasSubtitle={true}
                hasActions={true}
                textLines={5}
                height={300}
            />
        );
    if (!lab) return <Typography>No lab data</Typography>;

    const { logoSignedUrl, name, organizationNumber, contact } = lab;
    const { address, communication } = contact || {};
    const { phone, email, site } = communication || {};

    return (
        <>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    {logoSignedUrl ? (
                        <img
                            src={logoSignedUrl}
                            alt={name}
                            style={{
                                width: 80,
                                height: 80,
                                borderRadius: '50%',
                                objectFit: 'cover',
                                marginRight: 16,
                                boxShadow: '0 0 5px rgba(0,0,0,0.1)',
                            }}
                        />
                    ) : (
                        <Avatar sx={{ width: 80, height: 80, mr: 3, bgcolor: 'grey.500' }}>
                            {name?.charAt(0)?.toUpperCase()}
                        </Avatar>
                    )}
                    <Box>
                        <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>
                            {name}
                        </Typography>
                        {organizationNumber && (
                            <Typography variant="body2" color="text.secondary">
                                {translate('organization:ORGANIZATION_NUMBER')}: {organizationNumber}
                            </Typography>
                        )}
                    </Box>
                </Box>

                <LoadingButton variant="outlined" onClick={handleToggleLabStatus} loading={isDisableLoading}>
                    {isDisabled ? translate('organization:ENABLE_LAB') : translate('organization:DISABLE_LAB')}
                </LoadingButton>
            </Box>

            <Divider sx={{ mb: 3 }} />

            <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                        {translate('organization:ADDRESS:ADDRESS')}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                        <LocationOn sx={{ color: 'primary.main', mr: 1, fontSize: 20 }} />
                        <Box>
                            <FormattedAddress address={address} />
                        </Box>
                    </Box>
                </Grid>

                <Grid item xs={12} md={6}>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                        {translate('organization:CONTACT_INFO')}
                    </Typography>

                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                        <Phone sx={{ color: 'primary.main', mr: 1, fontSize: 20 }} />
                        <Typography variant="body1">{phone || 'Not provided'}</Typography>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                        <Email sx={{ color: 'primary.main', mr: 1, fontSize: 20 }} />
                        <Typography variant="body1">{email || 'Not provided'}</Typography>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Language sx={{ color: 'primary.main', mr: 1, fontSize: 20 }} />
                        <Typography variant="body1">{site || 'Not provided'}</Typography>
                    </Box>
                </Grid>
            </Grid>

            <Divider sx={{ mt: 3, mb: 3 }} />

            <Box sx={{ display: 'flex', alignItems: 'left' }}>
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={!isRequireException}
                            onChange={(e) => handleToggleRequireException(e.target.checked)}
                            disabled={isRequireExceptionLoading}
                        />
                    }
                    label={translate('organization:REQUIRE_NO_EXCEPTION_LABEL')}
                />
                {isTioTandvardPricingEnabled && (
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={isTioTandvardEnabled}
                                onChange={(e) => handleToggleTandvardEnabled(e.target.checked)}
                                disabled={isTandvardEnabledLoading}
                            />
                        }
                        label={translate('organization:ENABLE_TIOTANDVARD_PRICE_LIST')}
                    />
                )}
                <Box sx={{ minWidth: 200, ml: 'auto' }}>
                    <CurrencyInput
                        currency={{ code: currency || defaultActionSetCurrency || '' } as Currency}
                        onCurrencySelect={handleCurrencySelect}
                        disabled={isCurrencyLoading}
                    />
                </Box>
            </Box>
        </>
    );
});

LabInfo.displayName = 'LabInfo';

export default LabInfo;
